import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  AuctionItemSell,
  BidItem,
  BuyerOfferActionDto,
  ClearAllSelectedVehicles,
  FinanceSource,
  InventoryManagementOfferActions,
  InventoryManagementSearchResult,
  InventoryManagementSellerOffer,
  LaunchDarklyService,
  multiLanguageAlphaNumericRegexHelper,
  MultiOfferActionResponseDto,
  MultiOfferHistoryRequest,
  PurchaseActionService,
  SellerOfferAction,
  SellerOfferActionDto
} from 'remarketing-angular-library';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { get } from 'lodash';
import {
  CharacterCountComponent,
  CharacterCountDirective,
  CurrencyInputComponent,
  DatalistItemComponent,
  InputConfig,
  ModalComponent,
  SpinnerComponent,
  TooltipComponent
} from 'remarketing-ui-components-lib';
import { Store } from '@ngxs/store';
import { FeeToolTipService } from '../../../services/feetooltip/feetooltip.service';
import { OfferModalAction } from '../../../directives/offer-modal-action/offer-modal-action';
import { OfferModalDirective } from '../../../../vehicles/components/offer-modals/offer-modal.directive';
import { OfferModalItem } from '../../../models/offer-modal-item.model';
import { OffersService } from '../../../services/offers/offers.service';
import { OfferHistory } from '../../../models/offer-history.model';
import { AnalyticsService, CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { MultiOfferActionBannerService } from '@ally/remarketing/shared/services';
import { InventoryItemDataService } from '../../../../sell/lib/sell-shared/services/inventory-item-data.service';
import { OfferModalsHeaderComponent } from '../../../ui/comment/modal-actions/offer-header/offer-modals-header.component';
import { MultiOfferHistoryComponent } from '../multi-offer-history/multi-offer-history.component';
import { ActionSubmittingComponent } from '../../../ui/comment/modal-actions/action-submitting/action-submitting.component';
import { CommonModule } from '@angular/common';
import { OfferFilterService } from '../../../../inventory-management/services/offer-filter/offer-filter.service';

export interface SharedListMultiActionModalCompleted {
  payload: MultiOfferActionResponseDto;
  vehicleCount: number;
  auctionItemUuids: string[];
  totalPrice: number;
  action: string;
  userType: string;
}

@Component({
  selector: 'app-shared-list-multi-action-modal',
  templateUrl: './shared-list-multi-action-modal.component.html',
  styleUrls: ['./shared-list-multi-action-modal.component.scss'],
  providers: [InventoryItemDataService],
  imports: [
    TranslocoDirective,
    ModalComponent,
    SpinnerComponent,
    OfferModalsHeaderComponent,
    MultiOfferHistoryComponent,
    CharacterCountComponent,
    DatalistItemComponent,
    TooltipComponent,
    CurrencyInputComponent,
    ActionSubmittingComponent,
    CommonModule,
    ReactiveFormsModule,
    CharacterCountDirective
  ]
})
export class SharedListMultiActionModalComponent extends OfferModalAction implements OnInit, OnDestroy {
  @Output() modalEvent = new EventEmitter<any>();
  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild(OfferModalDirective) offerModalHost: OfferModalDirective;
  @ViewChild(CharacterCountDirective) characterCountDirective: CharacterCountDirective;
  @Input() modalId = 'shared-list-multi-action-modal';
  @Input() tenantName: string;
  @Input() userType: string;
  items: OfferModalItem[];
  private _bidItems: BidItem[] = [];
  private _auctionItemSells: AuctionItemSell[] | InventoryManagementSearchResult[] = [];

  private readonly destroyed$ = new Subject();

  item: OfferModalItem;
  selectedFinanceSources: FinanceSource[];
  offerHistoryRequest: MultiOfferHistoryRequest;
  waitingResponse = false;
  successfulResponse = false;
  taxesPresent = false;
  totalOfferAmount = 0;
  totalFees = 0;
  vehicleCount = 0;
  offers: Partial<SellerOfferActionDto>[];
  public isLoading = true;
  tooltipContent: any;
  allFees = [];
  singleVehicle = false;
  incorrectAmount = false;
  isAmountEmpty = false;
  /** From the bid list search response metadata; set by the buyer host before show(). */
  readonly platformSpareKeyFee = signal<number | undefined>(undefined);
  /** Buyer's spare key choice per vehicle, keyed by auctionItemUuid. */
  readonly spareKeySelections = signal<Record<string, boolean>>({});

  readonly CONSTANTS = {
    feesTooltipAriaLabel: '',
    feesAndTaxesTooltipAriaLabel: '',
    labelAccept: '',
    labelCounter: '',
    labelDecline: '',
    labelWithdraw: '',
    labelRevive: '',
    labelSuccess: '',
    textAcceptOffers: '',
    textDeclineOffers: '',
    textWithdrawOffers: '',
    textCounterBuyerOffer: '',
    textReviveOffer: '',
    textSellingDisclosure: '',
    textPermanentAction: '',
    btnAccept: '',
    btnCounter: '',
    btnDecline: '',
    btnCancel: '',
    btnWithdraw: '',
    btnRevive: '',
    labelComments: '',
    textActionWaitingOffers: '',
    labelOfferDeclined: '',
    labelTotalOfOffers: '',
    labelTotalFees: '',
    labelTotalPurchasePrice: '',
    labelSpareKeyTotal: '',
    labelErrorTitle: '',
    labelErrorSubTitle: '',
    counterOfferAmountError: '',
    emptyOfferAmountError: ''
  };

  errorMessage: string | ((val: string | number) => string);
  form: UntypedFormGroup;
  characterCountData: any;
  toolTipContent: any;
  currentModalType: string;
  isOpenedFromListView: boolean;
  currentModalId: string;
  sellingDisclosureUrl: string;
  isMobile = false;
  originalAmount: number;

  reviveInputConfig: InputConfig = {
    inputClass: 'form-control',
    inputName: 'reviveOfferAmount',
    inputFormControlName: 'reviveOfferAmount',
    inputAttrAriaLabelledBy: 'revive-offer-amount-label',
    thousandSeparator: '',
    isFrenchCanadian: this.isFrenchCanadian
  };

  counterInputConfig: InputConfig = {
    inputClass: 'form-control',
    inputName: 'counterOfferAmount',
    inputFormControlName: 'counterOfferAmount',
    inputAttrAriaLabelledBy: 'counter-offer-amount-label counter-offer-amount-error',
    thousandSeparator: '',
    isFrenchCanadian: this.isFrenchCanadian,
    inputInvalidClass: this.invalidClass
  };

  get invalidClass(): boolean {
    return this.incorrectAmount || this.isAmountEmpty;
  }

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly translocoService: TranslocoService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly offersService: OffersService,
    private readonly _analyticsService: AnalyticsService,
    private readonly purchaseActionService: PurchaseActionService,
    private readonly tooltipService: FeeToolTipService,
    private readonly store: Store,
    private readonly multiOfferActionBannerService: MultiOfferActionBannerService,
    private readonly inventoryItemDataService: InventoryItemDataService,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly offerFilterService: OfferFilterService
  ) {
    super();
  }

  get isInventoryManagementV2() {
    return this._launchDarklyService.getFeatureFlag('sim-v2Enabled', false);
  }

  set bidItems(items: BidItem[]) {
    this._bidItems = items;
    const auctionItemUuids = [];
    this.totalFees = 0;
    this.vehicleCount = 0;
    let fees = [];
    this.items = items.map(item => {
      auctionItemUuids.push(item.auctionItemUuid);
      this.totalFees += item.buyFee.total;
      fees = fees.concat(item.buyFee.fees);
      if (!this.taxesPresent) {
        this.taxesPresent = this.tooltipService.isTaxesPresent(item.buyFee);
      }
      this.vehicleCount++;
      return OfferModalItem.fromBidItem((item as unknown) as BidItem);
    });

    this.offerHistoryRequest = {
      auctionItemUuids
    };
    this.allFees = fees;
  }

  get bidItems() {
    return this._bidItems;
  }

  get auctionItemSells(): AuctionItemSell[] | InventoryManagementSearchResult[] {
    return this._auctionItemSells;
  }

  set auctionItemSells(items: AuctionItemSell[] | InventoryManagementSearchResult[]) {
    if(this.isInventoryManagementV2){
      if(this.isOpenedFromListView) {
        this.setupModalDataFromListViewV2(items as InventoryManagementSearchResult[]);
      } else {
        this.setupModalDataFromNonListViewV2(items as InventoryManagementSearchResult[]);
      }
    } else {
      this._auctionItemSells = items;
      const auctionItemUuids = [];
      this.totalFees = 0;
      this.vehicleCount = 0;
      let fees = [];
      this.items = items.map(item => {
        auctionItemUuids.push(item.auctionItemUuid);
        this.totalFees += item.buyFee.total;
        fees = fees.concat(item.buyFee.fees);
        if (!this.taxesPresent) {
          this.taxesPresent = this.tooltipService.isTaxesPresent(item.buyFee);
        }
        this.vehicleCount++;
        return OfferModalItem.fromAuctionItemSell((item as unknown) as AuctionItemSell);
      });
      this.offerHistoryRequest = {
        auctionItemUuids,
        buyerOeUuid: this.getBuyerOeUuid(items as AuctionItemSell[]),
        leadBuyerOeUuid: this.getLeadBuyerOeUuid(items as AuctionItemSell[])
      };
      this.singleVehicle = this.vehicleCount === 1;
      this.allFees = fees;
    }
  }

  setupModalDataFromListViewV2(items: InventoryManagementSearchResult[]): void {
    this.handleCommonV2DataSetup(items);

    const latestOffers = items[0].offers.filter(offer => offer.latestOffer === true);
    const actionType: InventoryManagementOfferActions =
      InventoryManagementOfferActions[this.currentModalType as keyof typeof InventoryManagementOfferActions];
    const latestOffersWithCurrentActionType = latestOffers.filter(offer => offer.offerActions.includes(actionType));

    if(latestOffersWithCurrentActionType?.length !== 1) {
      console.log('Invalid match to take offer action. More than one latest offers share the desired action type.');
    } else {
      const auctionItemUuids = [];
      const targetOffer = latestOffersWithCurrentActionType[0];
      this.totalFees += targetOffer?.buyFee?.total ? targetOffer.buyFee.total : 0;
      const fees = targetOffer?.buyFee?.fees ? targetOffer.buyFee.fees: [];

      if (!this.taxesPresent) {
        this.taxesPresent = this.tooltipService.isTaxesPresent(targetOffer.buyFee);
      }

      this.items = items.map(item => {
        auctionItemUuids.push(item.vehicleUuid);
        this.vehicleCount++;
        return OfferModalItem.fromInventoryManagementSearchResult((item as unknown) as InventoryManagementSearchResult,
          targetOffer);
      });

      this.offerHistoryRequest = {
        auctionItemUuids,
        buyerOeUuid: targetOffer.buyerOeUuid,
        leadBuyerOeUuid: targetOffer.leadBuyerOeUuid
      };
      this.singleVehicle = this.vehicleCount === 1;
      this.allFees = fees;
    }
  }

  setupModalDataFromNonListViewV2(items: InventoryManagementSearchResult[]): void {
    this.handleCommonV2DataSetup(items);
    const auctionItemUuids = [];
    let fees = [];

    this.items = items.map((item) => {
      const auctionItemLatestOffer = this.offerFilterService.filterLatestOfferByOfferList(item as InventoryManagementSearchResult);
      auctionItemUuids.push(item.vehicleUuid);
      this.totalFees += auctionItemLatestOffer.buyFee.total;
      fees = fees.concat(auctionItemLatestOffer.buyFee.fees);
      if (!this.taxesPresent) {
        this.taxesPresent = this.tooltipService.isTaxesPresent(auctionItemLatestOffer.buyFee);
      }
      this.vehicleCount++;
      return OfferModalItem.fromInventoryManagementSearchResult((item as unknown) as InventoryManagementSearchResult,
        auctionItemLatestOffer);
    });

    this.offerHistoryRequest = {
      auctionItemUuids,
      buyerOeUuid: this.getBuyerOeUuidV2(items as InventoryManagementSearchResult[]),
      leadBuyerOeUuid: this.getLeadBuyerOeUuidV2(items as InventoryManagementSearchResult[])
    };
    this.singleVehicle = this.vehicleCount === 1;
    this.allFees = fees;
  }

  handleCommonV2DataSetup(items: InventoryManagementSearchResult[]): void {
    this._auctionItemSells = items;
    this.totalFees = 0;
    this.vehicleCount = 0;
  }

  get isCanadianTenantEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isFrenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }

  get isInServiceVehiclesEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get declineButtonClass() {
    return this.isInServiceVehiclesEnabled ? 'btn btn-primary' : 'btn btn-destructive';
  }

  findOffersByAction(itemOffers: InventoryManagementSellerOffer[], action: InventoryManagementOfferActions) {
    return itemOffers.find(offer => offer.offerActions.includes(action));
  }

  getBuyerOeUuidV2(items: InventoryManagementSearchResult[]): string {
    const itemOffer = this.offerFilterService.filterLatestOfferByOfferList(items[0] as InventoryManagementSearchResult);
    if (this.currentModalType === 'WITHDRAW') {
      return this.findOffersByAction(items[0].offers, InventoryManagementOfferActions.WITHDRAW).buyerOeUuid;
    } else if (this.currentModalType === 'REVIVE') {
      return this.findOffersByAction(items[0].offers, InventoryManagementOfferActions.REVIVE).buyerOeUuid;
    } else {
      return itemOffer.buyerOeUuid;
    }
  }

  getLeadBuyerOeUuidV2(items: InventoryManagementSearchResult[]) {
    const itemOffer = this.offerFilterService.filterLatestOfferByOfferList(items[0] as InventoryManagementSearchResult);
    if (this.currentModalType === 'WITHDRAW') {
      return this.findOffersByAction(items[0].offers, InventoryManagementOfferActions.WITHDRAW).leadBuyerOeUuid;
    } else if (this.currentModalType === 'REVIVE') {
      return this.findOffersByAction(items[0].offers, InventoryManagementOfferActions.REVIVE).leadBuyerOeUuid;
    } else {
      return itemOffer.leadBuyerOeUuid;
    }
  }

  getBuyerOeUuid(items: AuctionItemSell[]) {
    if (this.currentModalType === 'WITHDRAW') {
      const item = items[0].offers.find(x => x.offerActions.includes(SellerOfferAction.WITHDRAW));
      return item.buyerOeUuid;
    } else if (this.currentModalType === 'REVIVE') {
      const item = items[0].offers.find(x => x.offerActions.includes(SellerOfferAction.REVIVE));
      return item.buyerOeUuid;
    } else {
      return items[0].offers[0].buyerOeUuid;
    }
  }

  getLeadBuyerOeUuid(items: AuctionItemSell[]) {
    if (this.currentModalType === 'WITHDRAW') {
      const item = items[0].offers.find(x => x.offerActions.includes(SellerOfferAction.WITHDRAW));
      return item.leadBuyerOeUuid;
    } else if (this.currentModalType === 'REVIVE') {
      const item = items[0].offers.find(x => x.offerActions.includes(SellerOfferAction.REVIVE));
      return item.leadBuyerOeUuid;
    } else {
      return items[0].offers[0].leadBuyerOeUuid;
    }
  }

  ngOnInit() {
    this.form = this._formBuilder.group({
      offerComment: [''],
      counterOfferAmount: ['', Validators.required],
      reviveOfferAmount: ['', Validators.required]
    });

    this.store
      .select(state => state.configuration.configuration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(configuration => {
        this.sellingDisclosureUrl = get(configuration, 'sellingDisclosureUrl', null);
      });
  }

  getVehicleTitle(item: OfferModalItem) {
    const { modelYear = '', make = '', model = '', trim = '', vin = '' } = item;
    return `${modelYear} ${make} ${model} ${trim} VIN ${vin}`;
  }

  formatCurrency(currency, withCents?) {
    return this.currencyFormatService.formatCurrency(currency, withCents);
  }

  getTranslations() {
    let keyParams: any = {
      UIComponents: {},
      shared: {},
      common: {}
    };

    if (this.isCurrentUserBuyer()) {
      keyParams = {
        ...keyParams,
        'UIComponents.sharedListMultiActionModalText.textAcceptOffers': {
          vehicleCount: this.vehicleCount,
          totalPrice: this.formatCurrency(this.totalOfferAmount, false)
        },
        'UIComponents.sharedListMultiActionModalText.textDeclineOffers': {
          vehicleCount: this.vehicleCount,
          totalPrice: this.formatCurrency(this.totalOfferAmount, false)
        },
        'UIComponents.sharedListMultiActionModalText.textSellingDisclosure': {
          sellingDisclosureUrl: this.sellingDisclosureUrl
        }
      };
    }

    if (this.singleVehicle) {
      keyParams = {
        ...keyParams,
        'UIComponents.sharedListMultiActionModalText.textAcceptBuyerOffer': {
          vehicleTitle: this.getVehicleTitle(this.items[0])
        },
        'UIComponents.sharedListMultiActionModalText.textDeclineBuyerOffer': {
          vehicleTitle: this.getVehicleTitle(this.items[0])
        },
        'UIComponents.sharedListMultiActionModalText.textWithdrawSellerOffer': {
          vehicleTitle: this.getVehicleTitle(this.items[0])
        },
        'UIComponents.sharedListMultiActionModalText.textCounterBuyerOffer': {
          vehicleTitle: this.getVehicleTitle(this.items[0])
        },
        'UIComponents.sharedListMultiActionModalText.textReviveOffer': {
          vehicleTitle: this.getVehicleTitle(this.items[0])
        },
        error: {}
      };
    } else if (!this.isCurrentUserBuyer()) {
      keyParams = {
        ...keyParams,
        'UIComponents.sharedListMultiActionModalText.textMultiAcceptBuyerOffer': {},
        'UIComponents.sharedListMultiActionModalText.textMultiDeclineBuyerOffer': {}
      };
    }

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(take(1))
      .subscribe(
        (translate: any[]) => {
          this.characterCountData = {
            textCharacters: translate[0].textCharacters,
            textEnterMax: translate[0].textEnterMax
          };
          this.CONSTANTS.labelAccept = translate[0].sharedListMultiActionModalText.labelAccept;
          this.CONSTANTS.labelCounter = translate[0].sharedListMultiActionModalText.labelCounter;
          this.CONSTANTS.labelDecline = translate[0].sharedListMultiActionModalText.labelDecline;
          this.CONSTANTS.labelWithdraw = translate[0].sharedListMultiActionModalText.labelWithdraw;
          this.CONSTANTS.labelRevive = translate[0].sharedListMultiActionModalText.labelRevive;
          this.CONSTANTS.labelComments = translate[0].sharedListMultiActionModalText.labelComments;
          this.CONSTANTS.labelSuccess = translate[0].sharedListMultiActionModalText.labelSuccess;
          this.CONSTANTS.textActionWaitingOffers = translate[0].sharedListMultiActionModalText.textActionWaitingOffers;
          this.CONSTANTS.textPermanentAction = translate[0].sharedListMultiActionModalText.textPermanentAction;
          this.CONSTANTS.labelTotalOfOffers = translate[0].sharedListMultiActionModalText.labelTotalOfOffers;
          this.CONSTANTS.labelTotalFees = translate[0].sharedListMultiActionModalText.labelTotalFees;
          this.CONSTANTS.labelTotalPurchasePrice = translate[0].sharedListMultiActionModalText.labelTotalPurchasePrice;
          this.CONSTANTS.labelOfferDeclined = translate[0].sharedListMultiActionModalText.labelOfferDeclined;
          this.CONSTANTS.labelSpareKeyTotal = translate[0].sharedListMultiActionModalText.labelSpareKeyTotal;
          this.CONSTANTS.feesTooltipAriaLabel = translate[1].feeTooltipModal.textMoreInformationAriaLabelFees;
          this.CONSTANTS.feesAndTaxesTooltipAriaLabel =
            translate[1].feeTooltipModal.textMoreInformationAriaLabelFeesAndTaxes;
          this.CONSTANTS.btnAccept = translate[2].btnAccept;
          this.CONSTANTS.btnCounter = translate[2].btnCounter;
          this.CONSTANTS.btnDecline = translate[2].btnDecline;
          this.CONSTANTS.btnCancel = translate[2].btnCancel;
          this.CONSTANTS.btnWithdraw = translate[2].btnWithdraw;
          this.CONSTANTS.btnRevive = translate[2].btnRevive;
          this.CONSTANTS.labelErrorTitle = translate[2].labelErrorTitle;
          this.CONSTANTS.labelErrorSubTitle = translate[2].labelErrorSubTitle;
          this.counterInputConfig.thousandSeparator = translate[2].currency.thousandSeparator;
          this.reviveInputConfig.thousandSeparator = translate[2].currency.thousandSeparator;
          this.CONSTANTS.textAcceptOffers = translate[3];
          this.CONSTANTS.textDeclineOffers = translate[4];
          this.setUniqueTranslations(translate);
          this.modal.show();
          setTimeout(() => {
            //timeout is necessary since we are working outside of the Angular ngZone
            //If blank modals occur and does not change, increase timeout.
            this.changeDetectorRef.markForCheck();
          }, 100);
        },
        error => {
          console.log(error);
          this.analyticsTrackError(error, null);
        }
      );
  }

  setUniqueTranslations(translate: any[]) {
    if (this.isCurrentUserBuyer()) {
      this.CONSTANTS.textSellingDisclosure = translate[5];
    }

    if (this.singleVehicle) {
      this.CONSTANTS.textWithdrawOffers = translate[5];
      this.CONSTANTS.textCounterBuyerOffer = translate[6];
      this.CONSTANTS.counterOfferAmountError = translate[8].counterOfferAmountError;
      this.CONSTANTS.emptyOfferAmountError = translate[8].enterValidAmount;
      this.CONSTANTS.textReviveOffer = translate[7];
    }
  }

  get isMultiActionBuyerModal() {
    return !this.singleVehicle && this.isCurrentUserBuyer();
  }

  validateAmount() {
    this.isAmountEmpty = false;
    this.incorrectAmount = false;
    const counterValue = this.form.controls.counterOfferAmount.value;

    if (!counterValue) {
      this.isAmountEmpty = true;
      this.analyticsTrackError(this.CONSTANTS.emptyOfferAmountError, '');
    } else {
      const counterAmount = parseInt(counterValue);
      this.isAmountEmpty = false;
      if (counterAmount <= this.offers[0].amount) {
        this.incorrectAmount = true;
        this.analyticsTrackError(this.CONSTANTS.counterOfferAmountError, '');
      } else {
        this.incorrectAmount = false;
      }
    }
    this.counterInputConfig.inputInvalidClass = this.invalidClass;
  }

  getOfferHistory(): Observable<OfferHistory[]> {
    return this.offersService.getMultipleOfferHistories$(this.offerHistoryRequest).pipe(takeUntil(this.destroyed$));
  }

  replaceInvalidCharacters(e) {
    e.target.value = multiLanguageAlphaNumericRegexHelper(e.target.value);
  }

  analyticsInfo(pageDetails: string) {
    this._analyticsService.trackEvent('app-tooltip', pageDetails);
  }

  analyticsTrackError(errorMessage, errorCode) {
    this._analyticsService.track('customError', errorMessage, errorCode);
  }

  analyticsVehiclesAccepted(inventoryItemUuids: string[]) {
    this._analyticsService.track('customEvent', {
      name: 'acceptMultipleListVehicles',
      type: 'CustomEvent',
      category: 'UserInteraction:CustomEvent',
      attr: {
        acceptedVehicleUuids: inventoryItemUuids,
        numVehiclesAccepted: inventoryItemUuids.length
      }
    });
  }

  analyticsVehiclesDeclined(inventoryItemUuids: string[]) {
    this._analyticsService.track('customEvent', {
      name: 'declineMultipleListVehicles',
      type: 'CustomEvent',
      category: 'UserInteraction:CustomEvent',
      attr: {
        declinedVehicleUuids: inventoryItemUuids,
        numVehiclesDeclined: inventoryItemUuids.length
      }
    });
  }

  isCurrentUserBuyer() {
    return this.userType === 'BUYER';
  }

  show(modalType: string, items: any[], isOpenedFromListView = false) {
    this.currentModalType = modalType;
    this.isOpenedFromListView = isOpenedFromListView;
    this.items = items;
    this.offers = items.map(item => item.offer);
    this.singleVehicle = items.length === 1;
    this.spareKeySelections.set({});
    if (this.isCurrentUserBuyer()) {
      this.bidItems = items;
    } else {
      this.auctionItemSells = items;
    }
    this.showModalContent();
  }

  setSelectedFinanceSources(financeSources: FinanceSource[]) {
    this.selectedFinanceSources = financeSources;
  }

  setPlatformSpareKeyFee(fee: number | undefined) {
    this.platformSpareKeyFee.set(fee);
  }

  /** Spare key purchase feature flag; LD targeting already scopes it to Avis US. */
  get spareKeyPurchaseEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('spareKey-purchaseEnabled', false);
  }

  /** Buyer accept only; sellers never get a fee from their hosts, so this also stays false for them. */
  get showSpareKey(): boolean {
    return (
      this.spareKeyPurchaseEnabled &&
      this.isCurrentUserBuyer() &&
      this.checkModalType('ACCEPT') &&
      this.platformSpareKeyFee() != null
    );
  }

  get spareKeyTotal(): number {
    if (!this.showSpareKey) {
      return 0;
    }
    const selections = this.spareKeySelections();
    const selectedCount = (this.items ?? []).filter(item => item.hasSpareKey && selections[item.auctionItemUuid]).length;
    return selectedCount * this.platformSpareKeyFee();
  }

  onSpareKeyChange({ auctionItemUuid, selected }: { auctionItemUuid: string; selected: boolean }) {
    this.spareKeySelections.update(selections => ({ ...selections, [auctionItemUuid]: selected }));
  }

  /** Buyer multi offer request; isSpareKeyIncludedInPurchase is only sent when the spare key UI is shown. */
  buildBuyerOfferActions(): BuyerOfferActionDto[] {
    const comment = this.form.controls.offerComment.value;
    const includeSpareKey = this.showSpareKey;
    const selections = this.spareKeySelections();
    return this.offers.map(({ offerUuid }) => {
      const offerAction: BuyerOfferActionDto = { offerUuid, requestedAction: this.currentModalType, comment };
      if (includeSpareKey) {
        const item = this.items?.find(modalItem => modalItem.offerHistory?.offer?.offerUuid === offerUuid);
        offerAction.isSpareKeyIncludedInPurchase = !!(item?.hasSpareKey && selections[item.auctionItemUuid]);
      }
      return offerAction;
    });
  }

  showModalContent() {
    this.errorMessage = undefined;
    this.isLoading = true;
    this.getOfferHistory()
      .pipe(take(1))
      .subscribe((offers: OfferHistory[]) => {
        this.offers = [];
        this.totalOfferAmount = 0;
        offers.forEach(offer => {
          this.totalOfferAmount += offer.offer.amount;
          this.offers.push({
            amount: offer.offer.amount,
            offerUuid: offer.offer.offerUuid
          });
          this.items.find(item => item.auctionItemUuid === offer.offer.auctionItemUuid).offerHistory = offer;
        });
        this.isLoading = false;
        this.getTranslations();
      });
  }

  checkModalType(modalType: string) {
    return this.currentModalType === modalType;
  }

  close() {
    this.form.reset();
    this.modal.close();
    this.successfulResponse = false;
    this.waitingResponse = false;
    this.errorMessage = undefined;
    this.spareKeySelections.set({});
  }

  submitAction() {
    if (this.isCurrentUserBuyer()) {
      this.errorMessage = undefined;
      this.showFlags();
      this.purchaseActionService
        .multiOfferAction(this.buildBuyerOfferActions())
        .pipe(take(1))
        .subscribe(
          response => {
            if (response) {
              this.waitingResponse = false;
              this.successfulResponse = true;
              const { vehicleCount, totalPrice } = this.calculateOfferTotals(response);
              const event = {
                payload: response,
                action: this.currentModalType,
                vehicleCount,
                totalPrice,
                userType: this.userType
              } as SharedListMultiActionModalCompleted;
              const multiOfferBanners = this.multiOfferActionBannerService.createBanners(event);
              const itemsAuctionItemUuids = this.getSuccessfulAuctionItemUuids(response);
              const itemsLength = itemsAuctionItemUuids.length;
              switch (this.currentModalType) {
                case 'ACCEPT':
                  this._analyticsService.fireBuyActionEvent('acceptBuyAction', itemsLength, itemsAuctionItemUuids);
                  this.analyticsVehiclesAccepted(this.getMultiOfferAnalyticsUuids(response));
                  break;
                case 'DECLINE':
                  this._analyticsService.fireBuyActionEvent('declineBuyAction', itemsLength, itemsAuctionItemUuids);
                  this.analyticsVehiclesDeclined(this.getMultiOfferAnalyticsUuids(response));
                  break;
                case 'WITHDRAW':
                  this._analyticsService.fireBuyActionEvent('withdrawBuyAction', itemsLength, itemsAuctionItemUuids);
                  break;
                case 'COUNTER':
                  this._analyticsService.fireBuyActionEvent('counterBuyAction', itemsLength, itemsAuctionItemUuids);
                  break;
                case 'REVIVE':
                  this._analyticsService.fireBuyActionEvent('reviveBuyAction', itemsLength, itemsAuctionItemUuids);
                  break;
              }
              this.modalEvent.emit(multiOfferBanners);
            }
          },
          error => {
            console.log(error);
            this.analyticsTrackError(error, null);
            // Keep the modal open with a generic message so the buyer can retry
            this.waitingResponse = false;
            this.errorMessage = `${this.CONSTANTS.labelErrorTitle} ${this.CONSTANTS.labelErrorSubTitle}`;
          }
        );
    } else if (this.userType === 'SELLER') {
      if (!this.singleVehicle) {
        this.showFlags();
      }
      this.callMultiSellerOfferAction();
    }
  }

  showFlags() {
    this.waitingResponse = true;
    this.successfulResponse = false;
  }

  callMultiSellerOfferAction() {
    this.purchaseActionService
      .multiSellerOfferAction(this.offers, this.currentModalType, this.form.controls.offerComment.value)
      .pipe(take(1))
      .subscribe(
        response => {
          if (response) {
            const successfulAuctionItemUuids = this.getSuccessfulAuctionItemUuids(response);
            const { vehicleCount, totalPrice } = this.calculateOfferTotals(response);
            const event = {
              payload: response,
              action: this.currentModalType,
              vehicleCount,
              auctionItemUuids: successfulAuctionItemUuids,
              totalPrice,
              userType: this.userType
            } as SharedListMultiActionModalCompleted;
            const multiOfferBanners = this.multiOfferActionBannerService.createBanners(event);
            this.store.dispatch(new ClearAllSelectedVehicles());
            this.waitingResponse = false;
            if (this.singleVehicle && response.errors.length === 1) {
              this.offers[0].amount = this.originalAmount;
              this.translocoService
                .selectTranslateObject(multiOfferBanners.translationCodeMapping)
                .pipe(take(1))
                .subscribe(resp => {
                  this.errorMessage = resp[0];
                  this.analyticsTrackError(this.errorMessage, '');
                });
            } else {
              this.successfulResponse = true;
              const itemsLength = successfulAuctionItemUuids.length;
              switch (this.currentModalType) {
                case 'ACCEPT':
                  this._analyticsService.fireBuyActionEvent('acceptBuyAction', itemsLength, successfulAuctionItemUuids);
                  this.analyticsVehiclesAccepted(this.getMultiOfferAnalyticsUuids(response));
                  break;
                case 'DECLINE':
                  this._analyticsService.fireBuyActionEvent(
                    'declineBuyAction',
                    itemsLength,
                    successfulAuctionItemUuids
                  );
                  this.analyticsVehiclesDeclined(this.getMultiOfferAnalyticsUuids(response));
                  break;
                case 'WITHDRAW':
                  this._analyticsService.fireBuyActionEvent(
                    'withdrawBuyAction',
                    itemsLength,
                    successfulAuctionItemUuids
                  );
                  break;
                case 'COUNTER':
                  this._analyticsService.fireBuyActionEvent(
                    'counterBuyAction',
                    itemsLength,
                    successfulAuctionItemUuids
                  );
                  break;
                case 'REVIVE':
                  this._analyticsService.fireBuyActionEvent('reviveBuyAction', itemsLength, successfulAuctionItemUuids);
                  break;
              }
              this.modalEvent.emit(multiOfferBanners);
            }
          }
        },
        error => {
          console.log(error);
          this.analyticsTrackError(error, null);
        }
      );
  }

  getMultiOfferAnalyticsUuids(response): string[] {
    return this.items
      .filter(item => {
        return response.offersProcessed.some(offerProcessed => {
          return offerProcessed.offerUuid === item.offerHistory.offer.offerUuid;
        });
      })
      .map(item => item.inventoryItemUuid);
  }

  getMultiOfferBuyerDeclineAnalyticsUuids(response): string[] {
    return this.items
      .filter(item => {
        return response.offersProcessed.some(offerProcessed => {
          return offerProcessed.auctionItemUuid === item.offerHistory.offer.auctionItemUuid;
        });
      })
      .map(item => item.inventoryItemUuid);
  }

  counter() {
    this.validateAmount();
    if (!this.incorrectAmount && !this.isAmountEmpty) {
      this.originalAmount = this.offers[0].amount;
      this.offers[0].amount = this.form.controls.counterOfferAmount.value;
      this.submitAction();
    }
  }

  revive() {
    this.originalAmount = this.offers[0].amount;
    this.offers[0].amount = this.form.controls.reviveOfferAmount.value;
    this.submitAction();
  }

  isSubmitting(): boolean {
    return this.waitingResponse && !this.successfulResponse;
  }

  calculateOfferTotals(response: MultiOfferActionResponseDto) {
    return response.offersProcessed.reduce(
      (totals, offer) => {
        totals.vehicleCount++;
        return totals;
      },
      { totalPrice: 0, vehicleCount: 0 }
    );
  }

  getSuccessfulAuctionItemUuids(response: MultiOfferActionResponseDto) {
    let uuids = this.items.map(x => x.auctionItemUuid);
    if (response.errors) {
      const errorUuids = response.errors.map(x => x.auctionItemUuid);
      uuids = uuids.filter(x => !errorUuids.includes(x));
    }
    return uuids;
  }

  get totalPurchasePrice() {
    return this.totalOfferAmount + this.totalFees + this.spareKeyTotal;
  }

  get feeTooltipMoreInformationLabel() {
    return this.taxesPresent ? this.CONSTANTS.feesAndTaxesTooltipAriaLabel : this.CONSTANTS.feesTooltipAriaLabel;
  }

  getFeeDetailsTooltip() {
    this.tooltipContent = this.tooltipService.getFeeValues(this.allFees);
    return {
      metaTitle: this.feeTooltipMoreInformationLabel,
      body: this.tooltipContent
    };
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
